/**
 * ToastContainer.tsx
 *
 * Fixed-position container for toast notifications.
 * Renders toasts from the toast store.
 */

import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useToastStore, type Toast } from '@/base/stores/toastStore';

// ============================================================================
// Toast Item Component
// ============================================================================

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const [isExiting, setIsExiting] = useState(false);

  // Auto-dismiss
  useEffect(() => {
    if (toast.duration === 0) return; // Persistent toast

    const timeout = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onDismiss(toast.id), 150);
    }, toast.duration || 5000);

    return () => clearTimeout(timeout);
  }, [toast.id, toast.duration, onDismiss]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 150);
  };

  // Icon and colors based on type
  const config = {
    success: {
      icon: CheckCircle,
      bg: 'bg-[var(--ems-success-muted)]',
      border: 'border-[var(--ems-success)]',
      iconColor: 'text-[var(--ems-success)]',
    },
    error: {
      icon: XCircle,
      bg: 'bg-[var(--ems-danger-muted)]',
      border: 'border-[var(--ems-danger)]',
      iconColor: 'text-[var(--ems-danger)]',
    },
    warning: {
      icon: AlertTriangle,
      bg: 'bg-[var(--ems-warning-muted)]',
      border: 'border-[var(--ems-warning)]',
      iconColor: 'text-[var(--ems-warning)]',
    },
    info: {
      icon: Info,
      bg: 'bg-[var(--ems-info-muted)]',
      border: 'border-[var(--ems-info)]',
      iconColor: 'text-[var(--ems-info)]',
    },
  }[toast.type];

  const Icon = config.icon;

  return (
    <div
      role="alert"
      className={`
        flex items-start gap-3 p-4 rounded-lg
        bg-[var(--grok-toast-bg)] border ${config.border}
        shadow-lg backdrop-blur-sm
        transition-all duration-150
        ${isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}
        animate-slide-up
      `}
    >
      <Icon size={20} className={`flex-shrink-0 mt-0.5 ${config.iconColor}`} />
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p className="text-sm font-medium text-[var(--grok-text-primary)]">{toast.title}</p>
        )}
        <p className="text-sm text-[var(--grok-text-secondary)]">{toast.message}</p>
      </div>
      <button
        onClick={handleDismiss}
        className="
          flex-shrink-0 p-1 rounded
          text-[var(--grok-text-muted)]
          hover:text-[var(--grok-text-primary)] hover:bg-[var(--grok-hover-bg)]
          focus:outline-none
        "
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function ToastContainer() {
  const { toasts, dismissToast } = useToastStore();

  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      aria-label="Notifications"
      className="
        fixed bottom-4 right-4 z-50
        flex flex-col gap-2
        max-w-sm w-full
        pointer-events-none
      "
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onDismiss={dismissToast} />
        </div>
      ))}
    </div>
  );
}

export default ToastContainer;
